import type { TFunction } from 'i18next'
import type { ActivityEntry } from './ActivityList'

export interface RawActivityTransaction {
  id: string
  type: string
  stamps_delta: number
  rewards_delta: number
  created_at: string
  cashier_name?: string | null
  actor_role?: string | null
  correction_reason?: string | null
  reward_item_name?: string | null
  earning_method?: string | null
  amount_per_stamp?: number | null
  trigger?: string | null
  stamp_target_before?: number | null
  stamp_target_after?: number | null
  stamps_before?: number | null
  stamps_after?: number | null
}

function formatTimestamp(dateString: string, t: TFunction, language: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const time = date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', hour12: false })

  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (isToday) return `${t('common.today')}, ${time}`

  const yesterdayDate = new Date(now)
  yesterdayDate.setDate(now.getDate() - 1)
  const isYesterday =
    date.getFullYear() === yesterdayDate.getFullYear() &&
    date.getMonth() === yesterdayDate.getMonth() &&
    date.getDate() === yesterdayDate.getDate()

  if (isYesterday) return `${t('common.yesterday')}, ${time}`

  const dayMonth = date.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${dayMonth}, ${time}`
}

function formatDelta(tx: RawActivityTransaction, t: TFunction): { delta?: string; isNegative: boolean } {
  if (tx.stamps_delta !== 0) {
    const sign = tx.stamps_delta > 0 ? '+' : ''
    const abs = Math.abs(tx.stamps_delta)
    const word = abs === 1 ? t('common.activity.stampSingle') : t('common.activity.stamps')
    return { delta: `${sign}${tx.stamps_delta} ${word}`, isNegative: tx.stamps_delta < 0 }
  }

  if (tx.rewards_delta !== 0) {
    const sign = tx.rewards_delta > 0 ? '+' : ''
    const abs = Math.abs(tx.rewards_delta)
    const word = abs === 1 ? t('common.activity.rewardSingle') : t('common.activity.rewards')
    return { delta: `${sign}${tx.rewards_delta} ${word}`, isNegative: tx.rewards_delta < 0 }
  }

  return { isNegative: false }
}

function resolveActorLabel(tx: RawActivityTransaction, t: TFunction): string {
  if (tx.cashier_name) return tx.cashier_name
  switch (tx.actor_role) {
    case 'manager': return t('common.activity.actor.manager')
    case 'admin':   return t('common.activity.actor.admin')
    default:        return t('common.activity.actor.cashier')
  }
}

function formatDetail(tx: RawActivityTransaction, t: TFunction): string | undefined {
  switch (tx.type) {
    case 'stamps_added':
      if (tx.earning_method === 'spend_based' && tx.amount_per_stamp) {
        return t('common.activity.detail.spendBased', { amount: tx.amount_per_stamp })
      }
      return t('common.activity.detail.perMeal')

    case 'referral_bonus':
      return t('common.activity.detail.referralBonus')

    case 'stamp_correction': {
      const actor = resolveActorLabel(tx, t)
      return t('common.activity.detail.byActor', { actor })
    }

    case 'reward_restored': {
      const actor = resolveActorLabel(tx, t)
      if (tx.reward_item_name) {
        return t('common.activity.detail.byActorWithItem', { actor, item: tx.reward_item_name })
      }
      return t('common.activity.detail.byActor', { actor })
    }

    case 'reward_unlocked':
      if (tx.trigger === 'target_reduction' && tx.stamp_target_before != null && tx.stamp_target_after != null) {
        return t('common.activity.detail.targetReduction', { targetBefore: tx.stamp_target_before, targetAfter: tx.stamp_target_after })
      }
      return tx.reward_item_name ?? undefined

    case 'reward_redeemed': {
      const actor = resolveActorLabel(tx, t)
      if (tx.reward_item_name) {
        return t('common.activity.detail.byActorWithItem', { actor, item: tx.reward_item_name })
      }
      return t('common.activity.detail.byActor', { actor })
    }

    case 'reward_expired':
      return tx.reward_item_name ?? undefined

    default:
      return undefined
  }
}

export function mapTransactionToActivity(
  tx: RawActivityTransaction,
  t: TFunction,
  language: string,
): ActivityEntry {
  const label = t(`common.activity.type.${tx.type}`, { defaultValue: tx.type })
  const detail = formatDetail(tx, t)
  const { delta, isNegative } = formatDelta(tx, t)
  const timestamp = formatTimestamp(tx.created_at, t, language)

  return { id: tx.id, label, detail, timestamp, delta, isNegative }
}